<template>
  <li class="tile" :class="dynamicClasses">
    <div class="tile__group">
      <div class="tile__heading">{{ heading }}</div>
      <div class="tile__body">{{ body }}</div>
    </div>
  </li>
</template>

<script lang="ts">
import { getHours, isSameDay } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
const DEFAULT_HEADING = 'Send a request';
const DEFAULT_BODY = 'Request your own time';

export default {
  name: 'AvailabilityCard',
  props: {
    availability: {
      type: Object,
      required: true,
    },
    timeZone: {
      type: String,
      default: () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  },
  computed: {
    dynamicClasses(): string {
      if (this.availability.node.kind) {
        return `tile--${this.availability.node.kind.toLowerCase()}`;
      }

      return 'tile--default';
    },
    heading(): string {
      if (!this.availability.node.start) return DEFAULT_HEADING;

      return formatInTimeZone(this.availability.node.start, this.timeZone, 'E, MMM d');
    },
    body(): string {
      const startIso = this.availability.node.start;
      if (!startIso) return DEFAULT_BODY;

      const endIso = this.availability.node.end;
      const tz = this.timeZone;
      // Local date-fns helpers then read the sitter's wall clock.
      const start = toZonedTime(startIso, tz);
      const end = toZonedTime(endIso, tz);
      const sameDay = isSameDay(start, end);
      const sameMeridiem = getHours(start) < 12 === getHours(end) < 12;

      if (sameDay && sameMeridiem) {
        return `${formatInTimeZone(startIso, tz, 'h:mm')} - ${formatInTimeZone(endIso, tz, 'h:mm aaa')}`;
      } else if (sameDay) {
        return `${formatInTimeZone(startIso, tz, 'h:mm aaa')} - ${formatInTimeZone(endIso, tz, 'h:mm aaa')}`;
      } else {
        return `${formatInTimeZone(startIso, tz, 'h:mm aaa')} - ${formatInTimeZone(endIso, tz, 'h:mm aaa (eee)')}`;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.tile {
  display: flex;
  padding: 1rem;
  border-bottom: 1px solid #f0f1f1;
  border-left: 4px solid theme('colors.success.DEFAULT');
  border-radius: 4px;
  background: var(--Surface-Foreground, #fff);
  /* +3 elevation */
  box-shadow: 0px 2px 4px 0px rgba(5, 7, 15, 0.16);

  &--available {
    border-left: 4px solid theme('colors.success.DEFAULT');
  }

  &--booked {
    border-left: 4px solid #76787e;

    .tile__heading {
      font-weight: normal;
    }
  }

  &__body {
    color: #282930;
    font-size: 0.875rem;
  }

  &__group {
    flex: 2;
  }

  &__heading {
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  &__action {
    flex: 1;
  }
}
</style>
